import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionStatus } from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Icon from 'flow/components/ui-components/Icon';

interface Props {
    contact?: TypedMap<ContactFull>;
    contactFullName: string;
    colleagueKey: ExtensionKey | null;
    inPanel: boolean;
    /** Hide the contact's own picture and show the default avatar instead. */
    ignoreAvatarResource?: boolean;
}

/**
 * Avatar for the contact summary card. Colleagues (contacts that map to an
 * internal extension) get a presence status indicator. With no saved name and
 * no picture, falls back to a neutral gray circle with an outlined person icon
 * (a track one step darker than the card) rather than initials or the filled
 * silhouette.
 */
const ContactCardAvatar = ({
    contact,
    contactFullName,
    colleagueKey,
    inPanel,
    ignoreAvatarResource,
}: Props) => {
    const colleagueStatus = useAppSelector((state) =>
        colleagueKey ? selectExtensionStatus(state, colleagueKey) : undefined,
    );

    const showPlaceholderAvatar =
        !contactFullName && !contact?.has('avatarResource');

    if (showPlaceholderAvatar) {
        return (
            <div
                className={twMerge(
                    'flex size-14 shrink-0 items-center justify-center rounded-full text-gray-500',
                    inPanel ? 'bg-gray-300' : 'bg-gray-200',
                )}
            >
                <Icon iconKey="personOutline" size="1.5rem" />
            </div>
        );
    }

    return (
        <ContactAvatar
            contact={contact}
            size="large"
            status={colleagueStatus}
            ignoreAvatarResource={ignoreAvatarResource}
        />
    );
};

export default React.memo(ContactCardAvatar);
